
import React, { useState, useEffect, useRef } from 'react';
import { GoogleGenAI, LiveServerMessage, Modality, Blob } from '@google/genai';
import { UserProfile, PersonalityType, Language } from '../types';
import { PERSONALITY_CONFIG } from '../constants';
import { TRANSLATIONS } from '../translations';
import { Mic, MicOff, Volume2, X, Sparkles, Loader2, MessageSquare, Info, Zap, Activity, Waves, Gauge, Ear, AlertCircle, RefreshCw, Presentation, BookOpen, Microscope, BarChart3, Binary, HelpCircle, CheckCircle2, ChevronRight, ZapOff } from 'lucide-react';

interface Props {
  user: UserProfile;
}

type SpeechMode = 'technical' | 'public' | 'poetic' | 'daily';

const LiveCompanion: React.FC<Props> = ({ user }) => {
  const [isActive, setIsActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [transcription, setTranscription] = useState<string[]>([]);
  const [liveInput, setLiveInput] = useState('');
  const [liveOutput, setLiveOutput] = useState('');
  const [visualScale, setVisualScale] = useState(1);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeMode, setActiveMode] = useState<SpeechMode>('daily');
  const [showGuide, setShowGuide] = useState(true);
  
  const t = TRANSLATIONS[user.language] || TRANSLATIONS[Language.KURDISH];
  
  const audioContextRef = useRef<AudioContext | null>(null);
  const inputContextRef = useRef<AudioContext | null>(null);
  const outputNodeRef = useRef<GainNode | null>(null);
  const nextStartTimeRef = useRef<number>(0);
  const sourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const sessionRef = useRef<any>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const currentInputRef = useRef('');
  const currentOutputRef = useRef('');

  const modes = [
    { id: 'technical', label: t.speech_mode_tech, icon: Microscope, color: 'text-blue-400', tip: t.speech_tip_tech },
    { id: 'public', label: t.speech_mode_public, icon: Presentation, color: 'text-emerald-400', tip: t.speech_tip_public },
    { id: 'poetic', label: t.speech_mode_poetic, icon: BookOpen, color: 'text-rose-400', tip: t.speech_tip_poetic },
    { id: 'daily', label: t.speech_mode_daily, icon: MessageSquare, color: 'text-amber-400', tip: user.language === Language.KURDISH ? 'بە ئاسوودەیی قسە بکە.' : 'با خیالی آسوده صحبت کن.' },
  ];

  const startSession = async () => {
    setShowGuide(false);
    setIsConnecting(true);
    setErrorMessage(null);
    setTranscription([]);
    currentInputRef.current = '';
    currentOutputRef.current = '';
    
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const AudioContextClass = (window.AudioContext || (window as any).webkitAudioContext);
      const inputCtx = new AudioContextClass({ sampleRate: 16000 });
      const outputCtx = new AudioContextClass({ sampleRate: 24000 });
      
      if (inputCtx.state === 'suspended') await inputCtx.resume();
      if (outputCtx.state === 'suspended') await outputCtx.resume();
      
      inputContextRef.current = inputCtx;
      audioContextRef.current = outputCtx;
      outputNodeRef.current = outputCtx.createGain();
      outputNodeRef.current.connect(outputCtx.destination);
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { echoCancellation: true, noiseSuppression: true } 
      });
      
      const analyser = inputCtx.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;
      const source = inputCtx.createMediaStreamSource(stream);
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateVisuals = () => {
        if (!analyserRef.current) return;
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setVolumeLevel(average);
        animationFrameRef.current = requestAnimationFrame(updateVisuals);
      };
      updateVisuals();

      const sessionPromise = ai.live.connect({
        model: 'gemini-2.5-flash-native-audio-preview-12-2025',
        callbacks: {
          onopen: () => {
            setIsActive(true);
            setIsConnecting(false);
            const scriptProcessor = inputCtx.createScriptProcessor(4096, 1, 1);
            scriptProcessor.onaudioprocess = (e) => {
              const inputData = e.inputBuffer.getChannelData(0);
              const pcmBlob = createBlob(inputData);
              sessionPromise.then(session => {
                if (session && isActive) session.sendRealtimeInput({ media: pcmBlob });
              });
            };
            source.connect(scriptProcessor);
            scriptProcessor.connect(inputCtx.destination);
          },
          onmessage: async (message: LiveServerMessage) => {
            if (message.serverContent?.outputTranscription) {
              currentOutputRef.current += message.serverContent.outputTranscription.text;
              setLiveOutput(currentOutputRef.current);
            } else if (message.serverContent?.inputTranscription) {
              currentInputRef.current += message.serverContent.inputTranscription.text;
              setLiveInput(currentInputRef.current);
            }

            if (message.serverContent?.turnComplete) {
              if (currentInputRef.current || currentOutputRef.current) {
                setTranscription(prev => [...prev.slice(-8), `User: ${currentInputRef.current}`, `Hawre: ${currentOutputRef.current}`]);
              }
              currentInputRef.current = '';
              currentOutputRef.current = '';
              setLiveInput('');
              setLiveOutput('');
            }

            const base64Audio = message.serverContent?.modelTurn?.parts[0]?.inlineData?.data;
            if (base64Audio && audioContextRef.current) {
              const audioCtx = audioContextRef.current;
              nextStartTimeRef.current = Math.max(nextStartTimeRef.current, audioCtx.currentTime);
              const audioBuffer = await decodeAudioData(decode(base64Audio), audioCtx, 24000, 1);
              const sourceNode = audioCtx.createBufferSource();
              sourceNode.buffer = audioBuffer;
              sourceNode.connect(outputNodeRef.current!);
              sourceNode.start(nextStartTimeRef.current);
              nextStartTimeRef.current += audioBuffer.duration;
              sourcesRef.current.add(sourceNode);
            }

            if (message.serverContent?.interrupted) {
              sourcesRef.current.forEach(s => { try { s.stop(); } catch(e) {} });
              sourcesRef.current.clear();
              nextStartTimeRef.current = 0;
            }
          },
          onerror: () => stopSession(),
          onclose: () => stopSession()
        },
        config: {
          responseModalities: [Modality.AUDIO],
          outputAudioTranscription: {},
          inputAudioTranscription: {},
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Zephyr' } },
          },
          systemInstruction: `You are Hawre, a real-time coach. Mode: ${activeMode}. Be concise and helpful.`
        }
      });
      sessionRef.current = await sessionPromise;
    } catch (error: any) {
      setIsConnecting(false);
      setErrorMessage(error.message || "Mic connection failed.");
    }
  };

  const stopSession = () => {
    if (sessionRef.current) try { sessionRef.current.close(); } catch(e) {}
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    sourcesRef.current.forEach(s => { try { s.stop(); } catch(e) {} });
    sourcesRef.current.clear();
    if (inputContextRef.current) inputContextRef.current.close();
    if (audioContextRef.current) audioContextRef.current.close();
    setIsActive(false);
    setIsConnecting(false);
    setShowGuide(true);
  };

  useEffect(() => { return () => stopSession(); }, []);

  function createBlob(data: Float32Array): Blob {
    const l = data.length;
    const int16 = new Int16Array(l);
    for (let i = 0; i < l; i++) int16[i] = data[i] * 32768;
    return { data: encode(new Uint8Array(int16.buffer)), mimeType: 'audio/pcm;rate=16000' };
  }
  function encode(bytes: Uint8Array) {
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }
  function decode(base64: string) {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
    return bytes;
  }
  async function decodeAudioData(data: Uint8Array, ctx: AudioContext, sampleRate: number, numChannels: number): Promise<AudioBuffer> {
    const dataInt16 = new Int16Array(data.buffer);
    const frameCount = dataInt16.length / numChannels;
    const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);
    for (let channel = 0; channel < numChannels; channel++) {
      const channelData = buffer.getChannelData(channel);
      for (let i = 0; i < frameCount; i++) channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
    return buffer;
  }

  return (
    <div className="h-full flex flex-col bg-[#050816] rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/5 animate-fade-in text-white relative">
      <div className="p-6 flex justify-between items-center border-b border-white/10 backdrop-blur-xl bg-white/5">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-2xl bg-indigo-600 shadow-lg`}>
            <Binary className="w-8 h-8 text-white" />
          </div>
          <div>
            <h3 className="font-black text-xl tracking-tight">{t.companion_live}</h3>
            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
              {isActive ? t.live_status_connected : isConnecting ? t.live_status_connecting : t.speech_coach_ready}
            </span>
          </div>
        </div>
        {isActive && (
          <button onClick={stopSession} className="px-5 py-2 bg-rose-500/20 rounded-2xl border border-rose-500/30 text-rose-400 text-xs font-black">
            {t.end_session}
          </button>
        )}
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center space-y-8">
        {showGuide && !isActive && !isConnecting && (
          <div className="max-w-md animate-slide-up">
            <h4 className="text-2xl font-black mb-4">Ready to talk?</h4>
            <p className="text-slate-400 mb-8">Choose a mode and start a live coaching session with Hawre.</p>
            <div className="grid grid-cols-2 gap-3 mb-8">
              {modes.map(m => (
                <button key={m.id} onClick={() => setActiveMode(m.id as SpeechMode)} className={`p-4 rounded-2xl border transition-all ${activeMode === m.id ? 'bg-white/10 border-blue-500 text-white' : 'bg-white/5 border-white/10 text-slate-500'}`}>
                   <m.icon className="w-5 h-5 mx-auto mb-2" />
                   <span className="text-xs font-bold">{m.label}</span>
                </button>
              ))}
            </div>
            <button onClick={startSession} className="w-full bg-blue-600 py-4 rounded-2xl font-black text-lg shadow-xl shadow-blue-600/20">
               Start Session
            </button>
          </div>
        )}

        {isConnecting && <Loader2 className="w-12 h-12 animate-spin text-blue-500" />}

        {isActive && (
          <div className="space-y-12 w-full max-w-lg">
             <div className="relative flex items-center justify-center">
                <div className="absolute w-48 h-48 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
                <div className="w-32 h-32 rounded-full border-4 border-blue-500 flex items-center justify-center relative z-10" style={{ transform: `scale(${1 + volumeLevel/50})` }}>
                   <Activity className="w-12 h-12 text-blue-400" />
                </div>
             </div>
             
             <div className="space-y-4">
                {liveInput && <div className="text-blue-400 italic text-sm">You: {liveInput}</div>}
                {liveOutput && <div className="text-emerald-400 font-bold">Hawre: {liveOutput}</div>}
             </div>

             <div className="bg-white/5 p-6 rounded-3xl border border-white/10 text-left">
                <h5 className="text-[10px] font-black uppercase text-slate-500 mb-4 tracking-widest">Transcription Log</h5>
                <div className="h-40 overflow-y-auto space-y-2 text-xs no-scrollbar">
                   {transcription.map((t, i) => <div key={i} className="opacity-60">{t}</div>)}
                </div>
             </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveCompanion;
